/**
 * Domain charge intent. Checkout never reads `threeDsUrl` or `wompi_3ds`.
 *
 * The edge may still emit those wire names — `chargeIntentFromCreatePayment`
 * is the adapter that translates them. A second PSP adds cases here, not
 * in the screen.
 */
export type PaymentChallengeKind = 'redirect' | 'hosted_approval'

export type PaymentChallenge = {
  kind: PaymentChallengeKind
  url: string
}

export type ChargeIntent = {
  paymentId: string
  transactionId: string
  provider: string
  status: 'pending' | 'captured' | 'failed'
  challenge: PaymentChallenge | null
}

export type CheckoutPayer = {
  email: string
  firstName: string
  lastName: string
  phone: string
}

/** Edge create-payment still wants this Spanish shape. Only the hook maps it. */
export type CreatePaymentPayerWire = {
  email: string
  nombre: string
  apellido: string
  telefono: string
}

export function payerToCreatePaymentWire(payer: CheckoutPayer): CreatePaymentPayerWire {
  return {
    email: payer.email,
    nombre: payer.firstName,
    apellido: payer.lastName,
    telefono: payer.phone,
  }
}

/** Edge create-payment still wants this Spanish card shape. Only the hook maps it. */
export function cardToCreatePaymentWire(card: {
  cardNumber: string
  cvv: string
  expiryMonth: number
  expiryYear: number
}): { numero: string; cvv: string; mes: number; anio: number } {
  return {
    numero: card.cardNumber,
    cvv: card.cvv,
    mes: card.expiryMonth,
    anio: card.expiryYear,
  }
}

export function checkoutPayerFromProfile(
  profile:
    | {
        email?: string | null
        firstName?: string | null
        lastName?: string | null
        phone?: string | null
      }
    | null
    | undefined,
  name?: { firstName?: string; lastName?: string },
): CheckoutPayer {
  return {
    email: profile?.email ?? '',
    firstName: name?.firstName || profile?.firstName || 'Cliente',
    lastName: name?.lastName || profile?.lastName || 'Tago',
    phone: profile?.phone ?? '',
  }
}

function readString(source: Record<string, unknown>, ...keys: string[]): string | null {
  for (const key of keys) {
    const value = source[key]
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return null
}

function challengeKindFromWire(
  mode: string | null,
  hasRedirect: boolean,
  hasHosted: boolean,
): PaymentChallengeKind | null {
  if (mode === 'paypal_app_switch' || mode === 'hosted_approval') return 'hosted_approval'
  if (mode === 'wompi_3ds' || mode === 'redirect_challenge' || mode === 'redirect')
    return 'redirect'
  if (hasHosted) return 'hosted_approval'
  if (hasRedirect) return 'redirect'
  return null
}

export function chargeIntentFromCreatePayment(data: unknown): ChargeIntent {
  const record = data && typeof data === 'object' ? (data as Record<string, unknown>) : {}
  const paymentUrl = readString(record, 'paymentUrl')
  const redirectUrl = readString(record, 'challengeUrl', 'threeDsUrl') ?? paymentUrl
  const mode = readString(record, 'checkoutMode')
  const hostedUrl =
    readString(record, 'approvalUrl') ??
    (mode === 'paypal_app_switch' || mode === 'hosted_approval' ? paymentUrl : null)
  const kind = challengeKindFromWire(mode, Boolean(redirectUrl), Boolean(hostedUrl))
  const url = kind === 'hosted_approval' ? hostedUrl : redirectUrl

  const statusRaw = readString(record, 'status')
  const status =
    statusRaw === 'captured' || statusRaw === 'failed' || statusRaw === 'pending'
      ? statusRaw
      : 'pending'

  return {
    paymentId: readString(record, 'paymentId') ?? '',
    transactionId: readString(record, 'transactionId') ?? '',
    provider: readString(record, 'provider') ?? '',
    status,
    challenge: kind && url ? { kind, url } : null,
  }
}
